import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaBookmark, FaCalendarAlt, FaTags, FaSpinner } from 'react-icons/fa';
import useGuides from '../hooks/useGuides';
import { formatDate } from '../utils/stringUtils';
import SEO from '../components/SEO';
import { Helmet } from 'react-helmet-async';
import { useDeviceDetection, getOptimizedBackgroundStyles } from '../utils/deviceUtils';

const GuideDetail = () => {
  const { slug } = useParams();
  const { getGuideBySlug, loading, error, guides } = useGuides();
  const navigate = useNavigate();
  const { isMobile } = useDeviceDetection();
  const guideItem = getGuideBySlug(slug);
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex flex-col items-center justify-center text-white">
        <FaSpinner className="animate-spin text-4xl text-accent-teal mb-4" />
        <p>Đang tải hướng dẫn...</p>
      </div>
    );
  }
  
  if (error || !guideItem) {
    return (
      <div className="min-h-screen bg-dark flex flex-col items-center justify-center text-white">
        <h2 className="text-2xl font-bold mb-4">Không tìm thấy hướng dẫn</h2>
        <button 
          onClick={() => navigate('/guides')}
          className="flex items-center px-4 py-2 bg-accent-teal/20 text-accent-teal rounded hover:bg-accent-teal/30 transition-all"
        >
          <FaArrowLeft className="mr-2" />
          Quay lại danh sách hướng dẫn
        </button>
      </div>
    );
  }
  
  // Background styles with optimization
  const backgroundStyles = getOptimizedBackgroundStyles({
    backgroundImage: `url(${guideItem?.image})`,
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    backgroundAttachment: isMobile ? 'scroll' : 'fixed'
  }, isMobile);

  return (
    <>
      <SEO 
        title={`${guideItem?.title} | PSA Roleplay`}
        url={`https://psa.world/guides/${guideItem?.slug}`}
        description={guideItem?.summary}
        image={guideItem?.image}
      />
      
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": guideItem?.title,
            "description": guideItem?.summary,
            "image": guideItem?.image,
            "datePublished": guideItem?.date,
            "publisher": {
              "@type": "Organization",
              "name": "PSA Roleplay",
              "logo": {
                "@type": "ImageObject",
                "url": "https://psa.world/resources/images/logo_new.png"
              }
            },
            // Extract steps from content if available or provide default step
            "step": guideItem?.steps?.map((step, index) => ({
              "@type": "HowToStep",
              "name": step.title || `Bước ${index + 1}`,
              "text": step.content,
              "url": `https://psa.world/guides/${guideItem?.slug}#step-${index + 1}`,
              "image": step.image || guideItem?.image
            })) || [{
              "@type": "HowToStep",
              "name": "Hướng dẫn",
              "text": guideItem?.summary,
              "url": `https://psa.world/guides/${guideItem?.slug}`,
              "image": guideItem?.image
            }],
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://psa.world/guides/${guideItem?.slug}`
            }
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Trang chủ",
                "item": "https://psa.world"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Tin tức tại PSA Roleplay",
                "item": "https://psa.world/guides"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": guideItem.title,
                "item": `https://psa.world/guides/${guideItem.slug}`
              }
            ]
          })}
        </script>
      </Helmet>
      
      <div className="min-h-screen pt-24 pb-16 bg-dark-purple-main relative overflow-hidden">
        {/* Dynamic background using the guide thumbnail */}
        <div 
          className="absolute inset-0 bg-center bg-cover opacity-30 bg-fixed"
          style={{ backgroundImage: `url(${guideItem?.image})` }}
        ></div>
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-dark-purple to-dark-purple-main"></div>
        
        <motion.div 
          className="container mx-auto px-4 relative z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {/* Navigation and breadcrumbs */}
          <motion.button
            onClick={() => navigate('/guides')}
            className="flex items-center px-4 py-2 mb-6 bg-dark/40 backdrop-blur-sm text-light rounded-lg hover:bg-dark/60 transition-all"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <FaArrowLeft className="mr-2" />
            Quay lại danh sách hướng dẫn
          </motion.button>
          
          {/* Guide header */}
          <div className="mb-10">
            <div className="flex items-center text-accent-teal mb-2">
              <FaBookmark className="mr-2" />
              <span className="text-sm font-medium uppercase tracking-wider">
                {guideItem?.category}
              </span>
            </div>
            
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
              {guideItem?.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-light/60">
              <div className="flex items-center">
                <FaCalendarAlt className="mr-2 text-accent-teal" />
                <span>{formatDate(guideItem?.date)}</span>
              </div>
              
              <div className="flex items-center">
                <FaTags className="mr-2 text-accent-teal" />
                <span>{guideItem?.tags?.join(', ')}</span>
              </div>
            </div>
          </div>
          
          {/* Separator line */}
          <motion.div
            className="w-24 h-1 bg-accent-teal mb-8"
            initial={{ width: 0 }}
            animate={{ width: 96 }}
            transition={{ delay: 0.5 }}
          />
          
          {/* Guide content */}
          <div className="glass-effect p-8 rounded-xl mb-8">
            <div className="prose prose-invert prose-accent-teal max-w-none">
              <div className="text-xl text-light/90 font-medium border-l-4 border-accent-teal pl-4 mb-8">
                {guideItem?.summary}
              </div>
              
              <div dangerouslySetInnerHTML={{ __html: guideItem?.content }} />
            </div>
          </div>
          
          {/* Related guides */}
          {guideItem?.related && guideItem.related.length > 0 && (
            <div className="mt-16">
              <h2 className="text-2xl font-bold mb-6 text-white">Hướng dẫn liên quan</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {guideItem.related.map((guide) => (
                  <Link 
                    key={guide.id}
                    to={`/guides/${guide.slug}`} 
                    className="glassmorphism-card overflow-hidden transform transition-all duration-300 hover:-translate-y-2"
                  >
                    <div 
                      className="h-48 bg-cover bg-center"
                      style={{ backgroundImage: `url(${guide.image})` }}
                    ></div>
                    <div className="p-4">
                      <div className="inline-block bg-accent-teal/20 text-accent-teal text-xs px-2 py-1 rounded-full mb-2">
                        {guide.category}
                      </div>
                      <h4 className="font-bold text-lg mb-2 text-white line-clamp-2">{guide.title}</h4>
                      <p className="text-light/70 text-sm line-clamp-2">{guide.summary}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </>
  );
};

export default GuideDetail;
